import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Wallet, Link2, Check, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useToast } from '../hooks/use-toast';

const ConnectWallet = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [walletAddress, setWalletAddress] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [showSecurityCheck, setShowSecurityCheck] = useState(false);
  const [securityGranted, setSecurityGranted] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState(1); // 1: Select wallet, 2: Security check, 3: Verification, 4: Connected

  const wallets = [
    { name: 'MetaMask', icon: '🦊', popular: true },
    { name: 'Trust Wallet', icon: '🛡️', popular: true },
    { name: 'Coinbase Wallet', icon: '💼', popular: false },
    { name: 'WalletConnect', icon: '🔗', popular: false },
  ];

  const features = [
    'Direct deposits & withdrawals',
    'Lower transaction fees',
    'Enhanced security',
    'Multi-chain support'
  ];

  const handleConnect = (walletName) => {
    setSelectedWallet(walletName);
    setShowSecurityCheck(true);
    setStep(2);
    toast({
      title: 'Security Check Required',
      description: `Please grant permission to connect ${walletName}`
    });
  };

  const handleGrantSecurity = () => {
    setSecurityGranted(true);
    setStep(3);
    toast({
      title: 'Permission Granted',
      description: 'Verifying wallet connection...'
    });
    // Simulate verification code generation
    setTimeout(() => {
      const code = Math.floor(100000 + Math.random() * 900000).toString();
      setVerificationCode(code);
    }, 1000);
  };

  const handleConfirmConnection = () => {
    toast({
      title: 'Connecting...',
      description: `Finalizing connection to ${selectedWallet}...`
    });
    setTimeout(() => {
      setIsConnected(true);
      setStep(4);
      setWalletAddress('0x742d35Cc6634C0532925a3b844Bc9e7595f0bEb');
      toast({ 
        title: 'Connected!', 
        description: 'Wallet connected successfully with full access'
      });
    }, 1500);
  };

  const handleDisconnect = () => {
    setIsConnected(false);
    setWalletAddress('');
    setShowSecurityCheck(false);
    setSecurityGranted(false);
    setSelectedWallet(null);
    setStep(1);
    toast({ title: 'Disconnected', description: 'Wallet disconnected successfully' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="sticky top-0 bg-slate-900/95 backdrop-blur-sm border-b border-cyan-500/20 z-10">
        <div className="flex items-center justify-between px-4 py-4">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-semibold">Back</span>
          </button>
          <h1 className="text-lg font-bold text-cyan-400">Connect Wallet</h1>
          <div className="w-16"></div>
        </div>
      </div>

      <div className="px-4 py-6">
        {isConnected ? (
          /* Connected State - Step 4 */
          <div>
            <div className="bg-gradient-to-r from-green-900/30 to-green-800/30 border border-green-500/30 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <div className="font-bold text-green-400">Wallet Connected</div>
                  <div className="text-xs text-green-600">Full access granted • Secure connection established</div>
                </div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3 mb-3">
                <div className="text-xs text-cyan-600 mb-1">Wallet Address</div>
                <div className="text-sm text-cyan-300 font-mono break-all">{walletAddress}</div>
              </div>
              <div className="bg-slate-900/50 rounded-lg p-3">
                <div className="text-xs text-cyan-600 mb-1">Connected Wallet</div>
                <div className="text-sm text-cyan-300 font-semibold">{selectedWallet}</div>
              </div>
            </div>

            <Button
              onClick={handleDisconnect}
              className="w-full h-12 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30"
            >
              Disconnect Wallet
            </Button>
          </div>
        ) : showSecurityCheck && step === 2 ? (
          /* Security Check - Step 2 */
          <div className="max-w-md mx-auto">
            <div className="bg-gradient-to-br from-yellow-900/20 to-orange-900/20 border-2 border-yellow-500/40 rounded-xl p-6 mb-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-yellow-500/20 flex items-center justify-center mb-4">
                  <AlertCircle className="w-8 h-8 text-yellow-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Security Verification Required</h2>
                <p className="text-cyan-300 text-sm">
                  {selectedWallet} needs permission to access your wallet
                </p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="bg-slate-900/50 border border-cyan-500/20 rounded-lg p-4">
                  <h3 className="text-white font-semibold mb-3">This connection will allow:</h3>
                  <ul className="space-y-2 text-sm text-cyan-300">
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>View your wallet balance and transaction history</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Request approval for transactions</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>Full access to deposit and withdraw funds</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3">
                  <p className="text-red-300 text-xs flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <span>Only grant access if you trust this connection. Never share your private keys or seed phrase.</span>
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={handleGrantSecurity}
                  className="w-full h-12 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500 text-white font-bold"
                >
                  Grant Permission & Continue
                </Button>
                <Button
                  onClick={() => { setShowSecurityCheck(false); setStep(1); }}
                  className="w-full h-12 bg-slate-700/50 hover:bg-slate-600 text-cyan-400"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        ) : showSecurityCheck && step === 3 ? (
          /* Verification - Step 3 */
          <div className="max-w-md mx-auto">
            <div className="bg-gradient-to-br from-cyan-900/20 to-blue-900/20 border-2 border-cyan-500/40 rounded-xl p-6 mb-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto rounded-full bg-cyan-500/20 flex items-center justify-center mb-4 animate-pulse">
                  <Wallet className="w-8 h-8 text-cyan-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Verifying Connection</h2>
                <p className="text-cyan-300 text-sm">
                  Please confirm this connection in your {selectedWallet}
                </p>
              </div>

              {verificationCode && (
                <div className="bg-slate-900/50 border border-cyan-500/30 rounded-lg p-6 mb-6 text-center">
                  <div className="text-cyan-400 text-sm mb-2">Verification Code</div>
                  <div className="text-4xl font-bold text-white tracking-wider mb-1">{verificationCode}</div>
                  <div className="text-cyan-600 text-xs">Confirm this code in your wallet</div>
                </div>
              )}

              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 text-sm text-cyan-300">
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-green-400" />
                  </div>
                  <span>Permission granted</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-cyan-300">
                  <div className="w-8 h-8 rounded-full bg-cyan-500/20 flex items-center justify-center animate-pulse">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full"></div>
                  </div>
                  <span>Establishing secure connection...</span>
                </div>
              </div>

              <Button
                onClick={handleConfirmConnection}
                className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold"
              >
                Confirm & Connect
              </Button>
            </div>
          </div>
        ) : (
          /* Select Wallet - Step 1 */
          <div>
            {/* Info Banner */}
            <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4 mb-6">
              <div className="flex gap-2">
                <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-cyan-600">
                  <p className="font-semibold text-cyan-400 mb-2">Why Connect a Wallet?</p>
                  <ul className="space-y-1 text-xs">
                    {features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <Check className="w-3 h-3 text-green-400" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Wallet Options */}
            <div>
              <h2 className="text-lg font-bold text-cyan-400 mb-4">Select Wallet</h2>
              {wallets.map((wallet, idx) => (
                <button
                  key={idx}
                  onClick={() => handleConnect(wallet.name)}
                  className="w-full bg-slate-800/50 border border-cyan-500/20 rounded-xl p-4 mb-3 hover:bg-slate-800/70 transition-all text-left"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="text-4xl">{wallet.icon}</div>
                      <div>
                        <div className="font-semibold text-cyan-300">{wallet.name}</div>
                        {wallet.popular && (
                          <div className="text-xs text-green-400">Popular</div>
                        )}
                      </div>
                    </div>
                    <ExternalLink className="w-5 h-5 text-cyan-400" />
                  </div>
                </button>
              ))}
            </div>

            {/* Manual Input */}
            <div className="mt-6">
              <h3 className="text-md font-semibold text-cyan-400 mb-3">Or Enter Manually</h3>
              <Input
                type="text"
                placeholder="Enter wallet address (0x...)" 
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                className="h-12 bg-slate-800/50 border-cyan-500/30 text-cyan-100 mb-3"
              />
              <Button
                onClick={() => handleConnect('Manual')}
                className="w-full h-12 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white neon-button"
              >
                <Link2 className="w-5 h-5 mr-2" />
                Connect Wallet
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectWallet;